import { inject } from '@angular/core';
import {
  CanActivateFn,
  GuardResult,
  MaybeAsync,
  RouterStateSnapshot
} from '@angular/router';
import { Store } from '@ngrx/store';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { EMPTY, of } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';

import { POST_LOGIN_REDIRECT_KEY } from '@core/constants/auth.constants';
import { selectAuthChecked, selectUser } from '@core/store/auth/auth.selectors';

export const authGuard: CanActivateFn = (
  _route,
  state: RouterStateSnapshot
): MaybeAsync<GuardResult> => {
  const store = inject(Store);
  const oidcSecurityService = inject(OidcSecurityService);

  // Wait for the OIDC check + profile fetch to complete before deciding
  return store.select(selectAuthChecked).pipe(
    filter((checked) => checked),
    take(1),
    switchMap(() => {
      const user = store.selectSignal(selectUser);
      if (user()) return of(true);

      // Persist the URL the user was trying to reach so it can be restored
      // after the Keycloak round-trip. The library's postLoginRoute (default `/`)
      // would otherwise drop the path and strand the user on the default dataspace.
      if (state.url && state.url !== '/') {
        sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, state.url);
      }

      // Not authenticated — redirect to Keycloak
      oidcSecurityService.authorize();
      return EMPTY; // Never completes — page will redirect to Keycloak
    })
  );
};
